import type { HealthMetricMap, IsoDate, LogMap, Measurement } from "./types.js";
import type { PredictionRow } from "../../shared/types.js";
import type { Settlement } from "./settlement.js";
import type { DayRates } from "./stats.js";
import { SEASON_1 } from "../../shared/plan.js";
import { addDays } from "./dates.js";
import { dayCompletion, questRank } from "./scoring.js";
import { ratesForDates } from "./stats.js";
import { settlePredictions } from "./settlement.js";

/**
 * 賽季成績單：生日賽季封存後大家都能查的最終結果。
 *
 * 全部明確綁 `SEASON_1`，**不能吃主程式的 `PLAN_*`**：那兩個已經是第二季，用它們算
 * 的話達標率會變成第二季的天數，而且不會報錯。數字重用既有的計分與結算函式，
 * 不另算一份。
 *
 * 備註與保健品不在這裡，它們不屬於成績。與 `src/domain/` 的鐵則一致：「今天」由
 * 呼叫端傳入。
 */

export type RankCounts = Record<"S" | "A" | "B" | "C" | "D", number>;

export interface SeasonReportInput {
  measurements: Measurement[];
  logs: LogMap;
  healthMetrics: HealthMetricMap;
  predictions: PredictionRow[];
  today: IsoDate;
}

export interface SeasonReport {
  startWeight: number;
  /** 沒有任何體重時是 null。 */
  finalWeight: number | null;
  finalDate: IsoDate | null;
  reached: boolean | null;
  /** 從起點到結算一共減了幾公斤，增加是負數。 */
  lost: number | null;
  /** 計畫期間的總天數。 */
  days: number;
  rates: DayRates;
  rankCounts: RankCounts;
  settlement: Settlement;
}

export function seasonReport({
  measurements, logs, healthMetrics, predictions, today
}: SeasonReportInput): SeasonReport {
  const dates: IsoDate[] = [];
  for (let date = SEASON_1.start; date <= SEASON_1.end; date = addDays(date, 1)) dates.push(date);

  const rankCounts: RankCounts = { S: 0, A: 0, B: 0, C: 0, D: 0 };
  for (const date of dates) {
    const { grade } = questRank(dayCompletion(logs[date] || {}, healthMetrics[date] || {}));
    rankCounts[grade as keyof RankCounts]++;
  }

  const settlement = settlePredictions(predictions, measurements, today);
  const startWeight = SEASON_1.startWeight as number;

  return {
    startWeight,
    finalWeight: settlement.finalWeight,
    finalDate: settlement.finalDate,
    reached: settlement.reached,
    lost: settlement.finalWeight === null
      ? null
      : Math.round((startWeight - settlement.finalWeight) * 10) / 10,
    days: dates.length,
    rates: ratesForDates(dates, logs, healthMetrics),
    rankCounts,
    settlement
  };
}
